<template>
  <div class="mat-trace">
    <!-- <div class="mat-trace__hd">
      <div class="mat-trace__title">物料操作轨迹</div>
      <div class="mat-trace__meta" v-if="materialCode">{{ materialCode }}</div>
    </div> -->
    <ul class="mat-trace__list">
      <li
        v-for="(node, idx) in normalizedNodes"
        :key="idx"
        class="mat-trace__item"
        :class="['mat-trace__item--' + node.status, { 'is-first': idx === 0, 'is-last': idx === normalizedNodes.length - 1 }]"
      >
        <!-- 左侧竖线 + 节点圆点 -->
        <div class="mat-trace__line">
          <span class="mat-trace__dot">
            <i v-if="node.status === 'current'" class="mat-trace__pulse"></i>
          </span>
        </div>

        <!-- 右侧内容 -->
        <div class="mat-trace__body">
          <div class="mat-trace__row mat-trace__row--top">
            <span class="mat-trace__type">
              <!-- <i class="mat-trace__icon" :class="'icon-inbound'  "></i> -->
              {{ node.IDTYNM }} 
            </span>
            <span class="mat-trace__time">{{ node.IDNO }}</span>
          </div>
       
          <div class="mat-trace__row mat-trace__row--bot"  >
            <span class="mat-trace__desc">操作日期：{{ node.CHKDTTM  }}</span>
            <span class="mat-trace__time" v-if="node.ACTQTY">
              用户：<em>{{ node.ADDUSER }}</em> 
            </span>
          </div>
          <div class="mat-trace__row mat-trace__row--bot"  >
            <span class="mat-trace__desc">操作数量：{{ node.ACTQTY }}</span>
            <span class="mat-trace__qty" v-if="node.ACTQTY">
              结余数量：<em>{{ node.BALQTY }}</em> 
            </span>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * 轻量物料操作轨迹时间轴（Vue 2.6，零第三方依赖）
 * 适合放在「物料详情」或「单据详情」底部，展示该物料的全生命周期操作节点。
 *
 * Props:
 *   materialCode   String   物料编码（可选，显示在标题右侧）
 *   nodes          Array    节点列表，每项字段见下方 normalizedNodes 说明
 *
 * node 字段：
 *   id         任意       节点唯一标识，建议传；不传用下标兜底
 *   type       String     节点类型，枚举: inbound | qc | outbound | return | transfer | adjust | scrap
 *   typeLabel  String     类型文案，如「入库」「质检合格」「生产领料」「调拨出库」
 *   time       String/Date 时间，任意 new Date() 能解析的格式都行
 *   operator   String     操作人
 *   docNo      String     关联单号（可选）
 *   desc       String     备注/库位/原因等描述（可选）
 *   qty        Number     涉及数量（可选），正数出库、负数入库、或在 typeLabel 里说明方向
 *   unit       String     数量单位（qty 存在时生效）
 *   status     String     节点状态: done(默认，已完成，灰/深色) | current(当前进行中，绿+脉冲) | warn(异常/驳回，橙红)
 */
export default {
  name: 'MaterialTraceTimeline',
  props: {
    materialCode: { type: String, default: '' },
    nodes: { type: Array, default: () => [] }
  },
  computed: {
    // 统一按时间倒序排列（最新在最上），并补默认 status
    normalizedNodes() {
      const list = this.nodes.map(n => ({
        status: 'done',
        ...n
      }));
      return list.slice().sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
    }
  },
  methods: {
    // H5 里常见的轻量时间格式：今天/昨天显示「今天 HH:mm」，其余显示「MM-DD HH:mm」，跨年补「YYYY-」
    formatTime(t) {
      if (!t) return '';
      const d = new Date(t);
      if (isNaN(d.getTime())) return String(t);
      const now = new Date();
      const pad = n => (n < 10 ? '0' + n : '' + n);
      const sameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();
      const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const hm = pad(d.getHours()) + ':' + pad(d.getMinutes());
      if (sameDay(d, now)) return '今天 ' + hm;
      if (sameDay(d, yesterday)) return '昨天 ' + hm;
      const md = pad(d.getMonth() + 1) + '-' + pad(d.getDate());
      if (d.getFullYear() !== now.getFullYear()) return d.getFullYear() + '-' + md + ' ' + hm;
      return md + ' ' + hm;
    }
  }
};
</script>

<style scoped>
/* ===== 容器 ===== */
.mat-trace {
  background: #fff;
  border-radius: 12px;
  padding: 2px 10px 4px;
  font-size: 14px;
  color: #323233;
  box-sizing: border-box;
}
.mat-trace__hd {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding-bottom: 10px;
  border-bottom: 1px solid #f2f3f5;
  margin-bottom: 4px;
}
.mat-trace__title {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}
.mat-trace__meta {
  font-size: 12px;
  color: #969799;
  font-family: Menlo, Consolas, monospace;
}

/* ===== 列表 ===== */
.mat-trace__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.mat-trace__item {
  position: relative;
  display: flex;
  padding: 10px 0 14px;
}
.mat-trace__item:last-child { padding-bottom: 6px; }

/* 左侧竖线 */
.mat-trace__line {
  position: relative;
  width: 20px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
}
.mat-trace__line::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #ebedf0;
  left: 50%;
  transform: translateX(-50%);
}
.mat-trace__item.is-first .mat-trace__line::before { top: 10px; }
.mat-trace__item.is-last .mat-trace__line::before { height: 10px; }

/* 节点圆点 */
.mat-trace__dot {
  position: relative;
  z-index: 1;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #c8c9cc;
  margin-top: 5px;
  box-sizing: border-box;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #c8c9cc;
}
.mat-trace__item--done .mat-trace__dot { background: #07c160; box-shadow: 0 0 0 1px #07c160; }
.mat-trace__item--current .mat-trace__dot { background: #07c160; box-shadow: 0 0 0 1px #07c160; }
.mat-trace__item--warn .mat-trace__dot { background: #ee0a24; box-shadow: 0 0 0 1px #ee0a24; }

/* 进行中脉冲动画 */
.mat-trace__pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(7, 193, 96, 0.25);
  transform: translate(-50%, -50%);
  animation: mat-trace-pulse 1.4s ease-out infinite;
}
@keyframes mat-trace-pulse {
  0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 0.8; }
  100% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
}

/* 右侧内容 */
.mat-trace__body {
  flex: 1;
  padding-left: 8px;
  min-width: 0;
}
.mat-trace__row { display: flex; align-items: center; line-height: 22px; }
.mat-trace__row--top { justify-content: space-between; margin-bottom: 2px; }
.mat-trace__row--mid { color: #969799; font-size: 12px; margin-bottom: 2px; }
.mat-trace__row--bot { color: #646566; font-size: 12px; justify-content: space-between; }

.mat-trace__type {
  font-size: 14px;
  font-weight: 500;
  color: #323233;
  display: inline-flex;
  align-items: center;
}
.mat-trace__item--current .mat-trace__type { color: #07c160; }
.mat-trace__item--warn .mat-trace__type { color: #ee0a24; }

.mat-trace__time {
  font-size: 12px;
  color: #080808;
  font-family: Menlo, Consolas, monospace;
  flex-shrink: 0;
  margin-left: 8px;
}

.mat-trace__op { width:40% }
.mat-trace__doc text { color: #646566; }

.mat-trace__desc {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 8px;
}
.mat-trace__qty {
  flex-shrink: 0;
  color: #323233;
}
.mat-trace__qty em {
  font-style: normal;
  font-weight: 600;
  color: #ff7d00;
  font-size: 13px;
  margin-right: 1px;
}
.mat-trace__item--warn .mat-trace__qty em { color: #ee0a24; }

/* ===== 类型图标（纯 CSS 画，不引图标库） ===== */
.mat-trace__icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 4px;
  border-radius: 50%;
  background: #e8f7ee;
  position: relative;
  vertical-align: -2px;
}
.mat-trace__icon::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 2px;
  background: #07c160;
  border-radius: 1px;
}
.mat-trace__icon::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) rotate(90deg);
  width: 8px;
  height: 2px;
  background: #07c160;
  border-radius: 1px;
}
.icon-qc::before { width: 7px; height: 7px; background: transparent; border: 2px solid #1989fa; border-radius: 50%; transform: translate(-50%,-55%); }
.icon-qc::after  { width: 4px; height: 2px; background: #1989fa; transform: translate(1px, 3px) rotate(45deg); }
.icon-outbound::before { width: 8px; height: 2px; background: #ff7d00; transform: translate(-60%,-50%); }
.icon-outbound::after  { width: 5px; height: 5px; border-right: 2px solid #ff7d00; border-top: 2px solid #ff7d00; transform: translate(2px,-50%) rotate(45deg); background: transparent; }
.icon-return::before   { width: 8px; height: 2px; background: #07c160; transform: translate(-40%,-50%); }
.icon-return::after    { width: 5px; height: 5px; border-left: 2px solid #07c160; border-bottom: 2px solid #07c160; transform: translate(-6px,-50%) rotate(45deg); background: transparent; }
.icon-transfer::before { width: 8px; height: 2px; background: #7232dd; }
.icon-transfer::after  { width: 0; height: 0; background: transparent; border: 3px solid transparent; border-left-color: #7232dd; transform: translate(3px,-50%) rotate(0); }
.icon-adjust::before   { width: 2px; height: 8px; background: #969799; }
.icon-adjust::after    { width: 8px; height: 2px; background: #969799; transform: translate(-50%,-50%) rotate(45deg); }
.icon-scrap::before    { width: 8px; height: 2px; background: #ee0a24; transform: translate(-50%,-50%) rotate(45deg); }
.icon-scrap::after     { width: 8px; height: 2px; background: #ee0a24; transform: translate(-50%,-50%) rotate(-45deg); }
</style>
